import React, { useEffect, useState } from "react";
import AudioLinesIcon from "@src/lib/icons/18px/audio-lines";
import MicrophoneOutlineIcon from "@src/lib/icons/18px/microphone-outline";
import MicrophoneSlashOutlineIcon from "@src/lib/icons/18px/microphone-slash-outline";
import PhoneHangUpOutlineIcon from "@src/lib/icons/18px/phone-hang-up-outline";
import { Button } from "@src/lib/ui/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import {
  OpusAgentVoice,
  VoiceStatus,
  isVoiceSupported,
} from "./useOpusAgentVoice";
import { VoiceOrb } from "./VoiceOrb";

const STATUS_LABELS: Record<VoiceStatus, string> = {
  idle: "",
  connecting: "Connecting…",
  listening: "Listening",
  thinking: "Working on it…",
  speaking: "Speaking",
};

// Ghost icon Buttons in the icon colours the composer's other buttons use.
const iconButton = "shrink-0 text-icon-secondary hover:text-icon-primary";

// The composer button that starts voice mode. Renders nothing in browsers
// without WebRTC or a microphone API.
export const OpusAgentVoiceToggle: React.FC<{ voice: OpusAgentVoice }> = ({
  voice,
}) => {
  // Resolved after mount so server and first client render agree.
  const [supported, setSupported] = useState(false);
  useEffect(() => setSupported(isVoiceSupported()), []);
  if (!supported) return null;
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={voice.start}
          disabled={voice.isActive}
          aria-label="Talk to Opus Agent"
          data-testid="opus-agent-voice-start"
          className={iconButton}
        >
          <AudioLinesIcon className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Voice mode</TooltipContent>
    </Tooltip>
  );
};

// Replaces the text box while a voice call is live: the orb, what is
// happening, mute and hang up. The transcript keeps rendering above as
// ordinary turns.
export const OpusAgentVoiceBar: React.FC<{ voice: OpusAgentVoice }> = ({
  voice,
}) => (
  <div
    data-testid="opus-agent-voice-bar"
    data-status={voice.status}
    className="flex flex-col items-center gap-1 px-2 pt-3 pb-1"
  >
    <VoiceOrb
      status={voice.status}
      muted={voice.muted}
      getInputLevel={voice.getInputLevel}
      getOutputLevel={voice.getOutputLevel}
    />
    <Text
      inline
      variant="fine-print-title"
      role="status"
      aria-live="polite"
      className="text-text-secondary"
    >
      {voice.muted && voice.status === "listening"
        ? "Muted"
        : STATUS_LABELS[voice.status]}
    </Text>
    <div className="flex items-center gap-2 pt-1">
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={voice.toggleMute}
            aria-label={voice.muted ? "Unmute" : "Mute"}
            aria-pressed={voice.muted}
            data-testid="opus-agent-voice-mute"
            className={cn(
              iconButton,
              voice.muted &&
                "bg-state-pressed text-icon-primary hover:bg-state-pressed",
            )}
          >
            {voice.muted ? (
              <MicrophoneSlashOutlineIcon className="size-4" />
            ) : (
              <MicrophoneOutlineIcon className="size-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{voice.muted ? "Unmute" : "Mute"}</TooltipContent>
      </Tooltip>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="destructive"
            size="icon"
            onClick={voice.end}
            aria-label="End voice mode"
            data-testid="opus-agent-voice-end"
            className="shrink-0"
          >
            <PhoneHangUpOutlineIcon className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>End voice mode</TooltipContent>
      </Tooltip>
    </div>
  </div>
);
